/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import Redirect from '@modules/routing/Redirect'

import { getHerokuCluster } from '@/lib/heroku'
import { deploymentUrl, resolveDeploymentUrlForEsCluster } from '@/lib/urlBuilder'

import type { RouteObject } from 'react-router-dom'

export function getHerokuRedirects(): RouteObject[] {
  return [...getHerokuClusterRedirects(), ...getStackDeploymentRedirects(), ...getPluginRedirects()]

  function getHerokuClusterRedirects(): RouteObject[] {
    const herokuCluster = getHerokuCluster()

    if (!herokuCluster) {
      return []
    }

    return [
      {
        path: '/',
        element: (
          <Redirect
            to={resolveDeploymentUrlForEsCluster(
              deploymentUrl,
              herokuCluster.regionId,
              herokuCluster.id,
            )}
          />
        ),
      },
    ]
  }

  function getStackDeploymentRedirects(): RouteObject[] {
    return [
      {
        path: '/region/:regionId/deployment/:clusterId',
        element: (
          <Redirect
            to={(params) => `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}`}
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/config',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/console',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/console`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/logs',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/logs-metrics`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/snapshots`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots/:snapshotName',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/snapshots/${params.snapshotName}`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/kibana`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/apm',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/apm`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/metrics',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/metrics`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/security',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/security`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/activity',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/activity/elasticsearch',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity/elasticsearch`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/activity/kibana',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity/kibana`
            }
          />
        ),
      },
      {
        path: '/deployments/resolve/cluster/${params.regionId}/:deploymentId/elasticsearch/logs',
        element: <Redirect to={(params) => `/deployments/${params.deploymentId}/logs-metrics`} />,
      },
      {
        path: '/deployments/:deploymentId/monitoring',
        element: <Redirect to={(params) => `/deployments/${params.deploymentId}/health`} />,
      },
    ]
  }

  function getPluginRedirects(): RouteObject[] {
    return [
      {
        path: '/plugins',
        element: <Redirect to='/extensions' />,
      },
      {
        path: '/plugins/create',
        element: <Redirect to='/extensions/create' />,
      },
      {
        path: '/plugins/:pluginId',
        element: <Redirect to={(params) => `/extensions/${params.pluginId}/edit`} />,
      },
    ]
  }
}
